import { useState, useMemo } from "react"
import { Bell, Plus } from "lucide-react"
import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import { ResourcesFilters } from "../components/ui/school/school-resources/resources-filters"
import { ResourceStatsCards } from "../components/ui/school/school-resources/resource-stats-cards"
import { AddResourceModal } from "../components/ui/school/school-resources/add-resource-modal"
import { ResourcesTable } from "../components/ui/school/school-resources/resources-table"
import { PaginationStats } from "../components/ui/school/students-management/pagination-stats"


// Mock data for resources
const mockResources = [
  {
    id: "1",
    title: "Introduction to Mathematics - Chapter 1",
    type: "PDF",
    targetClass: "JSS 1",
    uploadedBy: "Mr. Adeyemi",
    uploadDate: "2024-01-15",
    downloads: 145,
    size: "2.5 MB",
  },
  {
    id: "2",
    title: "English Grammar Basics Video Tutorial",
    type: "Video",
    targetClass: "JSS 2",
    uploadedBy: "Mrs. Okonkwo",
    uploadDate: "2024-01-14",
    downloads: 89,
    size: "125 MB",
  },
  {
    id: "3",
    title: "Physics Quiz - Motion and Forces",
    type: "Quiz",
    targetClass: "SS 2",
    uploadedBy: "Dr. Ibrahim",
    uploadDate: "2024-01-13",
    downloads: 203,
    size: "1.1 MB",
  },
  {
    id: "4",
    title: "Chemistry Practical Worksheet",
    type: "Worksheet",
    targetClass: "SS 1",
    uploadedBy: "Mrs. Johnson",
    uploadDate: "2024-01-12",
    downloads: 167,
    size: "850 KB",
  },
  {
    id: "5",
    title: "History Presentation - World War II",
    type: "Presentation",
    targetClass: "SS 3",
    uploadedBy: "Mr. Bello",
    uploadDate: "2024-01-11",
    downloads: 92,
    size: "15 MB",
  },
  {
    id: "6",
    title: "Biology Study Guide - Cell Structure",
    type: "PDF",
    targetClass: "JSS 3",
    uploadedBy: "Dr. Amadi",
    uploadDate: "2024-01-10",
    downloads: 178,
    size: "3.2 MB",
  },
  {
    id: "7",
    title: "Geography Video - Climate Zones",
    type: "Video",
    targetClass: "SS 1",
    uploadedBy: "Mrs. Ezeh",
    uploadDate: "2024-01-09",
    downloads: 112,
    size: "98 MB",
  },
  {
    id: "8",
    title: "Computer Science Quiz - Programming Basics",
    type: "Quiz",
    targetClass: "SS 2",
    uploadedBy: "Mr. Okafor",
    uploadDate: "2024-01-08",
    downloads: 156,
    size: "900 KB",
  },
]

const SchoolResourcesPage = () => {
  const [filters, setFilters] = useState({
    searchQuery: "",
    resourceType: "",
    targetClass: "",
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [resources, setResources] = useState(mockResources)
  const resultsPerPage = 10

  // Filter resources based on search and filters
  const filteredResources = useMemo(() => {
    return resources.filter((resource) => {
      const matchesSearch =
        filters.searchQuery === "" || resource.title.toLowerCase().includes(filters.searchQuery.toLowerCase())
      const matchesType = filters.resourceType === "" || resource.type === filters.resourceType
      const matchesClass = filters.targetClass === "" || resource.targetClass === filters.targetClass

      return matchesSearch && matchesType && matchesClass
    })
  }, [filters, resources])

  // Calculate pagination
  const totalPages = Math.ceil(filteredResources.length / resultsPerPage)
  const paginatedResources = filteredResources.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage)

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleAddResource = (newResource) => {
    const resource = {
      id: String(resources.length + 1),
      title: newResource.title,
      type: newResource.type,
      targetClass: newResource.targetClass,
      uploadedBy: "Current Admin",
      uploadDate: new Date().toISOString().split("T")[0],
      downloads: 0,
      size: "2.1 MB",
    }

    setResources([resource, ...resources])
    console.log("Resource added successfully:", resource)
  }

  // Calculate stats
  const stats = {
    totalResources: resources.length,
    totalDownloads: resources.reduce((sum, r) => sum + r.downloads, 0),
    activeContributors: new Set(resources.map((r) => r.uploadedBy)).size,
    resourcesThisMonth: resources.filter((r) => {
      const uploadDate = new Date(r.uploadDate)
      const now = new Date()
      return uploadDate.getMonth() === now.getMonth() && uploadDate.getFullYear() === now.getFullYear()
    }).length,
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* School Sidebar */}
      <SchoolSidebar />

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">School Resources</h1>
            <p className="text-gray-500 mt-1">Manage and share educational resources</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-3 rounded-full hover:bg-white transition border border-gray-200">
              <Bell className="text-gray-600 w-6 h-6" />
              <span className="absolute top-2 right-2 block h-2 w-2 rounded-full bg-red-500"></span>
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Add Resource</span>
            </button>
          </div>
        </header>

        {/* Stats Cards */}
        <ResourceStatsCards
          totalResources={stats.totalResources}
          totalDownloads={stats.totalDownloads}
          activeContributors={stats.activeContributors}
          resourcesThisMonth={stats.resourcesThisMonth}
        />

        {/* Filters Bar */}
        <ResourcesFilters onFilterChange={handleFilterChange} />

        {/* Resources Table */}
        <ResourcesTable resources={paginatedResources} />

        {/* Pagination Stats */}
        {filteredResources.length > 0 && (
          <div className="mt-6">
            <PaginationStats
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={filteredResources.length}
              resultsPerPage={resultsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </main>

      {/* Add Resource Modal */}
      <AddResourceModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleAddResource} />
    </div>
  )
}

export default SchoolResourcesPage